<template>
  <PopupEntry
    v-if="show"
    class="vehicle-tasks-popup"
    :show="show"
    :title="popupTitle"
    :aria-busy="loading ? 'true' : 'false'"
    cancelButton="Close"
    :saveButton="selectedVehicles.length > 1 ? `Save (${selectedVehicles.length} vehicles)` : 'Save'"
    :viewMode="!selectedVehicles.length || !details.CanChangeAvailability"
    :disabledSave="loading || saving || !selectedVehicles.length"
    :messages="messages"
    :successMessages="successMessages"
    :errorMessages="errorMessages"
    @update:show="close"
    @save="save"
  >
    <div v-if="loading" class="vehicle-tasks-loading" role="status">
      <img src="https://dsmlib.logistics.systems/Icons/Loading.gif" width="48" height="48" alt="" />
      <span>Loading vehicle tasks...</span>
    </div>
    <template v-else-if="details.InventoryNo">
      <TableBox
        :key="groupByVehicle ? 'grouped-tasks' : 'all-tasks'"
        title="Tasks"
        :tableData="rows"
        :dataType="{
          '#': 'number',
          Vehicle: 'string',
          'Task Name': 'string',
          'Due Date': 'date',
          Status: 'string',
          Remarks: 'string',
          'Workshop Job No': 'string',
          Category: 'string',
          Trips: 'string',
        }"
        :maxSelection="0"
        :hideBottomBlock="true"
        :groupBy="groupByVehicle ? 'Vehicle Group' : ''"
        :hideColumns="groupByVehicle ? ['Vehicle Group', 'Vehicle', 'Category', 'Trips'] : ['Vehicle Group']"
        emptyMessage="No Pending Tasks"
        :fitContentWidth="true"
        style="flex: 1; min-height: 0"
      />
      <CheckBoxV2 title="Group by Vehicle" :isChecked.sync="groupByVehicle" style="margin: 8px 0 0" />
      <div class="vehicle-availability-controls">
        <TableBox
          :key="availabilityTableRevision"
          title="Set Vehicle Availability"
          :tableData="availabilityRows"
          :selectedRow.sync="selectedVehicles"
          :showSelectedCheckBox="details.CanChangeAvailability"
          :loading="saving"
          :allowExport="false"
          :maxSelection="details.CanChangeAvailability && !saving ? -1 : 0"
          :hideSearch="true"
          :hideBottomBlock="true"
          :fitContentWidth="true"
          :hideColumns="['InventoryNo', 'InventoryCode', 'Availability', 'AvailabilityRemark']"
          :dataType="{ Vehicle: 'string', Category: 'string', 'Current Status': 'string', 'Current Remark': 'string' }"
          style="flex: 0 1 auto; min-height: 100px; max-height: 180px"
        />
        <div v-if="details.CanChangeAvailability" class="vehicle-availability-row">
          <RadioButtonSet
            labelKey="label"
            name="finished-trip-vehicle-availability"
            :isHorizontal="true"
            :showBox="true"
            title="Set Vehicle Availability"
            class="vehicle-availability-options"
            :buttons="availabilityOptions"
            :selected.sync="selectedAvailability"
            :isDisabled="saving || !selectedVehicles.length"
            style="display: flex; flex-shrink: 0; align-items: center"
          />
          <span v-if="!selectedVehicles.length" class="availability-selection-help">Select vehicles above to set availability.</span>
          <Input
            v-if="selectedAvailability && selectedAvailability.keyValue === '0'"
            placeholder="Vehicle Remark"
            :isRequired="true"
            :inputValue.sync="remark"
            :errorMessage.sync="remarkError"
            :isDisabled="saving"
            validator="text,150"
          />
        </div>
      </div>
    </template>
  </PopupEntry>
</template>

<script>
import { normalizeRows } from './vehicleTodo';

export default {
  data() {
    return {
      show: false,
      saving: false,
      loading: false,
      details: {},
      request: {},
      rows: [],
      groupByVehicle: false,
      vehicles: [],
      availabilityTableRevision: 0,
      selectedVehicles: [],
      selectedAvailability: null,
      availabilityOptions: [
        { label: 'Available', keyValue: '1' },
        { label: 'Unavailable', keyValue: '0' },
      ],
      remark: '',
      remarkError: '',
      errorMessages: [],
      successMessages: [],
      messages: [],
      confirmPendingTrips: false,
      revision: 0,
    };
  },
  computed: {
    popupTitle() {
      const vehicle = [this.details.InventoryNo, this.details.InventoryCode || this.request.vehiclecode]
        .filter(Boolean)
        .join(' - ');
      return vehicle ? `Vehicle Tasks - ${vehicle}` : 'Vehicle Tasks';
    },
    availabilityRows() {
      return this.vehicles.map((vehicle) => {
        const status =
          this.availabilityOptions.find((item) => item.keyValue === vehicle.Availability)?.label || 'Unknown';
        const tone = { Available: 'ok', Unavailable: 'bad' }[status] || 'neutral';
        return {
          Vehicle: this.escapeMessage([vehicle.InventoryNo, vehicle.InventoryCode].filter(Boolean).join(' - ')),
          Category: this.escapeMessage(vehicle.Category),
          'Current Status': `<span class="status-chip status-chip--${tone}">${status}</span>`,
          'Current Remark': this.escapeMessage(vehicle.AvailabilityRemark),
          InventoryNo: vehicle.InventoryNo,
          InventoryCode: vehicle.InventoryCode,
          Availability: vehicle.Availability,
          AvailabilityRemark: vehicle.AvailabilityRemark,
        };
      });
    },
  },
  methods: {
    // eslint-disable-next-line vue/no-unused-properties -- Opened from Vehicle Schedule through the component ref.
    async open(request) {
      const revision = ++this.revision;
      this.request = { ...request };
      this.details = {};
      this.rows = [];
      this.groupByVehicle = false;
      this.vehicles = [];
      this.selectedVehicles = [];
      this.selectedAvailability = null;
      this.remark = '';
      this.remarkError = '';
      this.errorMessages = [];
      this.successMessages = [];
      this.messages = [];
      this.confirmPendingTrips = false;
      this.saving = false;
      this.loading = true;
      this.show = true;
      try {
        await this.$nextTick();
        if (revision !== this.revision || !this.show) return;
        const [result, categoryResult] = await Promise.all([
          this.$apiCallV2('/Vehicle/GetVehicleScheduleTasks', request, 'post', null, false),
          this.$apiCallV2('/Master/GetListMaster', { masterfile: 'VEHICLECATEGORY' }),
        ]);
        const details = Array.isArray(result) ? result[0] : result;
        if (revision !== this.revision || !this.show) return;
        if (!details || String(details.Results) !== '0' || !details.InventoryNo) {
          this.errorMessages = [
            this.escapeMessage(details?.ErrMsg || 'Vehicle tasks could not be loaded. Close and try again.'),
          ];
          return;
        }
        this.request = { ...request, inventoryno: details.InventoryNo };
        this.details = details;
        const categories = Array.isArray(categoryResult) ? categoryResult : [];
        this.vehicles = (Array.isArray(details.Vehicles) ? details.Vehicles : [details]).map((vehicle) => {
          const code = String(vehicle.Category || '').trim();
          const category = categories.find(
            (item) =>
              String(item['Category Code'] || '')
                .trim()
                .toUpperCase() === code.toUpperCase(),
          );
          return { ...vehicle, Category: String(category?.Description || code).trim() };
        });
        const vehicleTrips = new Map();
        (Array.isArray(details.VehicleJobs) ? details.VehicleJobs : []).forEach((item) => {
          const key = String(item.InventoryNo || '')
            .trim()
            .toUpperCase();
          const tripNo = String(item.TripNo || item.JobNo || '').trim();
          const eta = String(item.ETA || '').trim() || '-';
          const tripLabel = `${tripNo} (ETA: ${eta})`;
          const trips = vehicleTrips.get(key) || [];
          if (tripNo && !trips.some((trip) => trip.toUpperCase() === tripLabel.toUpperCase())) trips.push(tripLabel);
          vehicleTrips.set(key, trips);
        });
        this.rows = normalizeRows(details.Tasks || []).map((row, index) => {
          const vehicleType = String((details.Tasks[index] || {})['Vehicle Type'] || '').trim();
          const categoryCode = String(row['Vehicle Category'] || '').trim();
          const matchingCategories = categories.filter(
            (item) =>
              String(item['Category Code'] || '')
                .trim()
                .toUpperCase() === categoryCode.toUpperCase(),
          );
          const category = matchingCategories.find(
            (item) =>
              String(item.Type || '')
                .trim()
                .toUpperCase() === vehicleType.toUpperCase(),
          );
          const categoryName = String(
            category?.Description || matchingCategories[0]?.Description || categoryCode,
          ).trim();
          const typeName = String(category?.['Type Description'] || vehicleType).trim();
          const trips =
            vehicleTrips.get(
              String(row['Vehicle No.'] || '')
                .trim()
                .toUpperCase(),
            ) || [];
          const group = [
            row.Vehicle,
            categoryName || typeName ? `Category: ${[categoryName, typeName].filter(Boolean).join(' - ')}` : '',
            trips.length ? `Trips: ${trips.slice().sort().join(', ')}` : '',
          ]
            .filter(Boolean)
            .join(' · ');
          return {
            '#': row['#'],
            Vehicle: this.escapeMessage(row.Vehicle),
            'Vehicle Group': this.escapeMessage(group),
            'Task Name': row['Task Name'],
            'Due Date': row['Due Date'],
            Status: row.Status,
            Remarks: row.Remarks,
            'Workshop Job No': row['Workshop Job No'],
            Category: this.escapeMessage([categoryName, typeName].filter(Boolean).join(' - ')),
            Trips: this.escapeMessage(trips.slice().sort().join(', ')),
          };
        });
      } catch (error) {
        if (revision === this.revision && this.show)
          this.errorMessages = ['Vehicle tasks could not be loaded. Close and try again.'];
      } finally {
        if (revision === this.revision) this.loading = false;
      }
    },
    close() {
      this.revision++;
      this.show = false;
      this.loading = false;
      this.saving = false;
      this.$emit('closed');
    },
    escapeMessage(value) {
      return String(value || '').replace(
        /[&<>"']/g,
        (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char],
      );
    },
    async save() {
      if (this.loading || this.saving || !this.show || !this.details.CanChangeAvailability)
        return;
      const revision = this.revision;
      this.errorMessages = [];
      this.successMessages = [];
      this.messages = [];
      this.remarkError = '';
      if (!this.selectedVehicles.length) {
        this.errorMessages = ['Select at least one vehicle.'];
        return;
      }
      const availability = this.selectedAvailability?.keyValue;
      const remark = this.remark.trim();
      if (!availability) {
        this.errorMessages = ['Select Available or Unavailable.'];
        return;
      }
      if (availability === '0' && (!remark || remark.length > 150)) {
        this.remarkError = !remark ? 'Vehicle Remark is required.' : 'Vehicle Remark must not exceed 150 characters.';
        this.errorMessages = [this.remarkError];
        return;
      }
      this.saving = true;
      try {
        const response = await this.$apiCallV2(
          '/Vehicle/UpdateVehicleAvailability',
          {
            vehicles: this.selectedVehicles.map((vehicle) => ({
              inventoryno: vehicle.InventoryNo,
            })),
            availability,
            remark: availability === '0' ? remark : '',
            confirmpendingtrips: this.confirmPendingTrips,
          },
          'post',
          null,
          false,
        );
        if (revision !== this.revision || !this.show) return;
        const result = Array.isArray(response) ? response[0] : response;
        if (result && String(result.Results) === '3') {
          this.confirmPendingTrips = true;
          this.messages = [this.escapeMessage(result.ErrMsg)];
          return;
        }
        if (!result || String(result.Results) !== '0') {
          this.confirmPendingTrips = false;
          this.errorMessages = [this.escapeMessage(result?.ErrMsg || 'Vehicle availability not saved. Please retry.')];
          return;
        }
        const updated = Array.isArray(result.Vehicles) ? result.Vehicles : [];
        this.vehicles = this.vehicles.map((vehicle) => ({
          ...vehicle,
          ...updated.find(
            (item) =>
              String(item.InventoryNo).trim().toUpperCase() === String(vehicle.InventoryNo).trim().toUpperCase(),
          ),
        }));
        const primary = this.vehicles.find((vehicle) => vehicle.InventoryNo === this.details.InventoryNo);
        if (primary) this.details = { ...this.details, ...primary };
        this.selectedVehicles = [];
        this.confirmPendingTrips = false;
        this.availabilityTableRevision++;
        this.successMessages = ['Vehicle availability saved.'];
      } catch (error) {
        if (revision === this.revision && this.show)
          this.errorMessages = ['Vehicle availability not saved. Please retry.'];
      } finally {
        if (revision === this.revision) this.saving = false;
      }
    },
  },
  watch: {
    selectedVehicles() {
      if (!this.selectedVehicles.length) {
        this.selectedAvailability = null;
        this.remark = '';
        this.remarkError = '';
      }
      this.confirmPendingTrips = false;
      this.messages = [];
    },
    selectedAvailability() {
      this.confirmPendingTrips = false;
      this.messages = [];
    },
    remark() {
      this.confirmPendingTrips = false;
      this.messages = [];
    },
  },
  beforeDestroy() {
    this.revision++;
  },
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';
.vehicle-tasks-popup {
  font-size: 12px;
  padding: 0 30px 0 54px;
  box-sizing: border-box;
}
.vehicle-tasks-popup ::v-deep .popup-content {
  > .header, > .body, > .footer {
    padding: 8px 12px;
  }
  > .header > .title { margin-left: 0; }
  > .footer > button:last-child { margin-right: 0; }
}
.vehicle-tasks-loading {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 16px;
  min-height: 200px;
  color: black;
}
.vehicle-availability-controls {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: 8px;
  margin: 12px 0 0;
  font-size: 12px;
}
.vehicle-availability-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 5px 8px;
  > .input-main {
    flex: 1 1 180px;
    min-width: 180px;
  }
}
.availability-selection-help {
  color: grey;
  font-size: 11px;
}
.vehicle-availability-options ::v-deep .radio-buttons-container {
  align-items: center;
  justify-content: center;
}
</style>
